import { api } from '../../api/client';

/**
 * Tiny cache for `CrudField.previewUrl` values (e.g. the next Permit ID shown on
 * a New form).
 *
 * WHY THIS EXISTS. Fetching the preview when New is clicked gives you a choice of
 * two bad behaviours: render straight away and the value visibly pops in a moment
 * later, or hold the form back and New feels slow. Both were reported.
 *
 * So the value is fetched EARLY - while the user is still looking at the list -
 * and read synchronously when the form opens. The form then renders immediately
 * AND complete, with no second render that changes what is on screen.
 *
 * The cached value is advisory, exactly like the preview itself: the real id is
 * minted server-side inside the save transaction. Staleness is therefore
 * harmless, but it is still bounded - entries expire, and every entry is dropped
 * after a successful create, since that create is precisely what consumed the
 * number being previewed.
 */

interface Entry { value: unknown; at: number }

const cache = new Map<string, Entry>();
const inFlight = new Map<string, Promise<void>>();

/** How long a cached preview stays usable. Short: it is only meant to bridge the
 *  gap between the list rendering and the user clicking New. */
const TTL_MS = 60_000;

function fresh(e: Entry | undefined): e is Entry {
  return !!e && Date.now() - e.at < TTL_MS;
}

/**
 * Warm the cache for any GET returning the `{ data: ... }` envelope, storing the
 * WHOLE payload. Used to prefetch a record the user is about to open, and the
 * lookup lists its form needs.
 *
 * Safe to call repeatedly - a fresh entry or an in-flight request short-circuits,
 * so hovering the same row twice costs nothing. It is also SILENT: the request
 * opts out of the global loading indicator, because a speculative fetch the user
 * never asked for must not flash the progress bar.
 *
 * Failures are swallowed. A prefetch is an optimisation, never a correctness
 * requirement - on a miss the real open simply fetches as it always did.
 */
export function prefetchGet(url: string): void {
  if (fresh(cache.get(url)) || inFlight.has(url)) return;
  // `as never`: silentLoading is our own per-request flag, read by the client's
  // interceptor but absent from AxiosRequestConfig - the same cast every other
  // silent caller in the app uses.
  const p = api.get<{ data?: unknown }>(url, { silentLoading: true } as never)
    .then((res: { data?: { data?: unknown } }) => {
      const v = res.data?.data;
      if (v != null) cache.set(url, { value: v, at: Date.now() });
    })
    .catch(() => { /* an optimisation only - never surfaced */ })
    .finally(() => { inFlight.delete(url); });
  inFlight.set(url, p);
}

/** The cached payload for a GET, or undefined when absent or expired.
 *  Synchronous by design: callers read it during their first render. */
export function readGet(url: string): unknown {
  const e = cache.get(url);
  return fresh(e) ? e.value : undefined;
}

/** Warm the cache for one preview URL. A thin wrapper over {@link prefetchGet} so
 *  previews and prefetched records share ONE cache and ONE invalidation. */
export function prefetchPreview(url: string, key: string): void {
  void key;   // the whole payload is cached; readPreview digs the field out
  prefetchGet(url);
}

/** One previewed FIELD out of a cached payload, or undefined when absent or
 *  expired. Synchronous by design: the New form reads it during its first render. */
export function readPreview(url: string, key: string): unknown {
  const payload = readGet(url);
  return payload && typeof payload === 'object'
    ? (payload as Record<string, unknown>)[key] ?? undefined
    : undefined;
}

/** Drop everything cached. Called after any write: a create consumes the
 *  previewed number, and an update makes a prefetched record stale. Clearing the
 *  lot is deliberate - these are small lookups plus one record, and being exactly
 *  right after a write matters more than keeping a few of them warm. */
export function invalidatePreviews(): void {
  cache.clear();
}
